import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, inr, inrShort, requestErrorMessage } from "@/lib/erp";
import { downloadCSV } from "@/lib/erpdocs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, BarChart3, Printer } from "lucide-react";
import PrintBrandHeader from "@/components/PrintBrandHeader";
import { REPORT_GROUPS, reportPath } from "@/lib/reportCatalog";

const Kpi = ({ label, value, tone }) => (
  <Card className="p-4"><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className={`font-heading text-xl font-bold mt-1 tabular ${tone || ""}`}>{value}</div></Card>
);

function DataTable({ cols, rows, keys, right = [] }) {
  return (
    <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
      <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">{cols.map((c, i) => <th key={i} className={`px-4 py-3 font-medium ${right.includes(i) ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, ri) => (<tr key={ri} className="border-t hover:bg-slate-50">{keys.map((k, ci) => (<td key={ci} className={`px-4 py-3 ${right.includes(ci) ? "text-right font-mono" : ""}`}>{r[k]}</td>))}</tr>))}
      {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-muted-foreground"><BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-40" />No data</td></tr>}</tbody>
    </table></div></Card>
  );
}

export default function Reports() {
  const [d, setD] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [retry, setRetry] = useState(0);
  const [companyRetry, setCompanyRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    http.get("/reports/all")
      .then((response) => { if (active) setD(response.data); })
      .catch((requestError) => {
        if (active) setError(requestErrorMessage(requestError, "load reports"));
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [retry]);
  useEffect(() => {
    let active = true;
    setCompanyError("");
    http.get("/settings")
      .then((response) => { if (active) setCompany(response.data); })
      .catch((requestError) => {
        if (active) setCompanyError(requestErrorMessage(requestError, "load company details"));
      });
    return () => { active = false; };
  }, [companyRetry]);
  if (loading && !d) return <div className="p-8 text-muted-foreground">Loading reports…</div>;
  if (!d) return (
    <div className="p-8 space-y-3" role="alert">
      <p className="text-rose-700">{error || "Reports are unavailable."}</p>
      <Button size="sm" variant="outline" onClick={() => setRetry((value) => value + 1)}>Retry</Button>
    </div>
  );

  const money = (v) => inr(v);
  const salesRows = d.sales_register.map((r) => ({ ...r, taxable_f: money(r.taxable), tax_f: money(r.tax), total_f: money(r.total) }));
  const purcRows = d.purchase_register.map((r) => ({ ...r, taxable_f: money(r.taxable), tax_f: money(r.tax), total_f: money(r.total) }));
  const recvRows = d.receivables.map((r) => ({ ...r, current_f: money(r.current), d30_f: money(r.d30), d60_f: money(r.d60), d90_f: money(r.d90), total_f: money(r.total) }));
  const payRows = d.payables.map((r) => ({ ...r, total_f: money(r.total) }));
  const stockRows = d.stock_valuation.map((r) => ({ ...r, cost_f: money(r.cost), value_f: money(r.value) }));
  const itemRows = d.item_wise.map((r) => ({ ...r, taxable_f: money(r.taxable), total_f: money(r.total) }));

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PrintBrandHeader company={company} title="Reports" />
      {companyError && <div className="flex flex-wrap items-center gap-3 text-sm text-amber-700" role="status">
        <span>{companyError}. Report data is still available.</span>
        <Button size="sm" variant="outline" onClick={() => setCompanyRetry((value) => value + 1)}>Retry branding</Button>
      </div>}
      {error && <div className="flex items-center gap-3 text-sm text-rose-700" role="alert"><span>{error}</span><Button size="sm" variant="outline" onClick={() => setRetry((value) => value + 1)}>Retry</Button></div>}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Reports Center</h1><p className="text-sm text-muted-foreground">Sales, purchase, outstanding, stock & item analytics</p></div>
        <Button size="sm" variant="print" onClick={() => window.print()} data-testid="reports-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi label="Total Sales" value={inrShort(d.totals.sales)} />
        <Kpi label="Total Purchases" value={inrShort(d.totals.purchases)} />
        <Kpi label="Receivable" value={inrShort(d.totals.receivable)} tone="text-amber-600" />
        <Kpi label="Payable" value={inrShort(d.totals.payable)} tone="text-rose-600" />
        <Kpi label="Stock Value" value={inrShort(d.totals.stock_value)} tone="text-emerald-600" />
      </div>

      <div className="no-print">
        <h2 className="font-heading text-lg font-semibold mb-3">Detailed reports</h2>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {REPORT_GROUPS.map((group) => (
            <Card key={group.label} className="p-4">
              <h3 className="font-heading font-semibold mb-3">{group.label}</h3>
              <div className="flex flex-col gap-1">
                {group.reports.map((report) => (
                  <Link key={report.key} to={reportPath(report.key)}
                    className="rounded-md px-2 py-1.5 text-sm text-brand-700 hover:bg-brand-50 hover:underline">
                    {report.label}
                  </Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Tabs defaultValue="sales">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="sales" data-testid="tab-sales-register">Sales Register</TabsTrigger>
          <TabsTrigger value="purchase" data-testid="tab-purchase-register">Purchase Register</TabsTrigger>
          <TabsTrigger value="receivables" data-testid="tab-receivables">Receivables</TabsTrigger>
          <TabsTrigger value="payables" data-testid="tab-payables">Payables</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock-valuation">Stock Valuation</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-item-wise">Item-wise</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4 space-y-3">
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => downloadCSV("sales_register.csv", ["Invoice", "Date", "Customer", "Taxable", "Tax", "Total", "Status"], d.sales_register.map((r) => [r.invoice_no, r.date, r.customer, r.taxable, r.tax, r.total, r.status]))}><Download className="h-4 w-4 mr-1" /> CSV</Button></div>
          <DataTable cols={["Invoice", "Date", "Customer", "Taxable", "Tax", "Total", "Status"]} keys={["invoice_no", "date", "customer", "taxable_f", "tax_f", "total_f", "status"]} rows={salesRows} right={[3, 4, 5]} />
        </TabsContent>
        <TabsContent value="purchase" className="mt-4 space-y-3">
          <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => downloadCSV("purchase_register.csv", ["Doc", "Bill", "Date", "Vendor", "Taxable", "Tax", "Total"], d.purchase_register.map((r) => [r.doc_no, r.bill_no, r.date, r.vendor, r.taxable, r.tax, r.total]))}><Download className="h-4 w-4 mr-1" /> CSV</Button></div>
          <DataTable cols={["Doc No", "Bill No", "Date", "Vendor", "Taxable", "Tax", "Total"]} keys={["doc_no", "bill_no", "date", "vendor", "taxable_f", "tax_f", "total_f"]} rows={purcRows} right={[4, 5, 6]} />
        </TabsContent>
        <TabsContent value="receivables" className="mt-4">
          <DataTable cols={["Customer", "Current", "31-60d", "61-90d", "90d+", "Total"]} keys={["party", "current_f", "d30_f", "d60_f", "d90_f", "total_f"]} rows={recvRows} right={[1, 2, 3, 4, 5]} />
        </TabsContent>
        <TabsContent value="payables" className="mt-4">
          <DataTable cols={["Vendor", "Doc No", "Outstanding"]} keys={["party", "doc_no", "total_f"]} rows={payRows} right={[2]} />
        </TabsContent>
        <TabsContent value="stock" className="mt-4">
          <DataTable cols={["Product", "Batch", "Stock", "Cost", "Value"]} keys={["name", "batch_no", "stock", "cost_f", "value_f"]} rows={stockRows} right={[2, 3, 4]} />
        </TabsContent>
        <TabsContent value="items" className="mt-4">
          <DataTable cols={["Product", "Qty Sold", "Taxable", "Total"]} keys={["name", "qty", "taxable_f", "total_f"]} rows={itemRows} right={[1, 2, 3]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
